import { Settings as SettingsType } from '@anispi-reporting-system/types';
import { Box, Card, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React, { useEffect, useReducer, useState } from 'react';
import { useSnackbarMessage } from '../../hooks/useSnackbarMessage';
import { getSettings, saveSettings } from '../../lib/ApiSettings';
import { Button } from '../Button';
import { SnackbarMessage } from '../SnackbarMessage';

const gridColumnClassName = 'flex h-20 items-center border-b border-gray-300';

const defaultSettings: SettingsType = {
  openTimeSetting: {
    close: {
      date: 20,
    },
    open: {
      date: 1,
    },
  },
};

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useReducer(() => true, false);
  const [settingLoading, setSettingLoading] = useState(false);
  const { onClose, snackbarMessage, setSnackbarMessage } = useSnackbarMessage();

  const [settings, setSettings] = useState<SettingsType>(defaultSettings);

  useEffect(() => {
    (async () => {
      if (loaded === false) {
        setSettingLoading(true);
        try {
          const settingsResult = await getSettings();
          if (settingsResult.success) {
            setSettings(settingsResult.settings);
          }
        } finally {
          setLoaded();
          setSettingLoading(false);
        }
      }
    })();
  }, [loaded, settings]);

  const handleSave = async () => {
    setLoading(true);

    try {
      const saveResult = await saveSettings({
        settings,
      });

      if (saveResult && saveResult.success) {
        setSnackbarMessage({
          message: '保存しました',
          open: true,
          severity: 'success',
        });

        setSettings(saveResult.settings);
      } else {
        setSnackbarMessage({
          message: '保存に失敗しました',
          open: true,
          severity: 'error',
        });
      }
    } catch {
      setSnackbarMessage({
        message: '保存に失敗しました',
        open: true,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {settingLoading ? (
        <div className="flex justify-center">
          <div className="inline-block align-bottom">
            <CircularProgress size={40} />
          </div>
        </div>
      ) : (
        <>
          <Card variant="outlined" className="space-y-4 !overflow-y-scroll p-4">
            <Typography variant="h5">フォームの公開期間</Typography>
            <Grid container>
              <Grid item xs={1} className={gridColumnClassName}>
                開始
              </Grid>
              <Grid item xs={4} className={gridColumnClassName}>
                <Box className="flex flex-row space-x-4">
                  <FormControl className="w-40">
                    <InputLabel>日</InputLabel>
                    <Select
                      value={settings.openTimeSetting.open.date}
                      label="日"
                      onChange={e =>
                        setSettings({
                          ...settings,
                          openTimeSetting: {
                            close: settings.openTimeSetting.close,
                            open: {
                              ...settings.openTimeSetting.open,
                              date: typeof e.target.value === 'number' ? e.target.value : 1,
                            },
                          },
                        })
                      }
                    >
                      {[...new Array(15)].map((_, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <MenuItem key={index} value={index + 1}>
                          {index + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={1} className={gridColumnClassName}>
                終了
              </Grid>
              <Grid item xs={4} className={gridColumnClassName}>
                <Box className="flex flex-row space-x-4">
                  <FormControl className="w-40">
                    <InputLabel>日</InputLabel>
                    <Select
                      value={settings.openTimeSetting.close.date.toString()}
                      label="日"
                      onChange={e =>
                        setSettings({
                          ...settings,
                          openTimeSetting: {
                            close: {
                              date: typeof e.target.value === 'number' ? e.target.value : 20,
                            },
                            open: settings.openTimeSetting.open,
                          },
                        })
                      }
                    >
                      {[...new Array(12)].map((_, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <MenuItem key={index} value={index + 20}>
                          {index + 20}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Card>
          <Box className="flex flex-row justify-end space-x-4 py-4">
            <Button className="w-40" variant="outlined" onClick={handleSave} loading={loading}>
              設定を保存する
            </Button>
          </Box>
        </>
      )}
      <SnackbarMessage
        open={snackbarMessage.open}
        severity={snackbarMessage.severity}
        message={snackbarMessage.message}
        onClose={onClose}
      />
    </Box>
  );
};

export { Settings };
